/**
 * Ausbuchen (G-12): Auswahl aus der Eingebucht-Liste (inkl. Vortags-Ueberhang),
 * Stunden 1–12; das Backend friert dabei die Zulagen ein.
 */

import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { fmtDate } from "@/lib/format"
import { api, apiError } from "@/services/api"
import type { AttendanceRecord } from "@/types"

interface CheckOutSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Eingebucht-Liste (heute + Vortags-Ueberhang) */
  records: AttendanceRecord[]
  /** Vorauswahl (Zeilen-Aktion im Board) */
  preselectedId?: string | null
}

export function CheckOutSheet({
  open,
  onOpenChange,
  records,
  preselectedId,
}: CheckOutSheetProps) {
  const queryClient = useQueryClient()
  const [recordId, setRecordId] = useState("")
  const [hours, setHours] = useState("8")

  useEffect(() => {
    if (open) {
      setRecordId(preselectedId ?? "")
      setHours("8")
    }
    // Vorauswahl ist beim Oeffnen fix
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const record = records.find((r) => r.id === recordId) ?? null
  const hoursNum = Number(hours.replace(",", "."))
  const valid = record !== null && !Number.isNaN(hoursNum) && hoursNum >= 1 && hoursNum <= 12

  const checkOut = useMutation({
    mutationFn: () =>
      api.attendance.checkOut({ attendanceRecordId: Number(recordId), hours: hoursNum }),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["attendance"] })
      queryClient.invalidateQueries({ queryKey: ["shiftPlan"] })
      toast.success(`${r.employeeName} ausgebucht (${hours} h, Zulagen eingefroren)`)
      onOpenChange(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="attendance-check-out" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Ausbuchen</SheetTitle>
          <SheetDescription>
            Stunden 1–12; beim Ausbuchen werden die Zulagen eingefroren (G-12/G-15).
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Eingebuchte Buchung *</Label>
            <Select value={recordId} onChange={(e) => setRecordId(e.target.value)}>
              <option value="">
                {records.length === 0 ? "— niemand eingebucht —" : "— Buchung wählen —"}
              </option>
              {records.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.employeeName} — {fmtDate(r.date)}, {r.shiftName ?? "ohne Schicht"} /{" "}
                  {r.positionName}
                </option>
              ))}
            </Select>
          </div>

          {record && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
              {record.employeeName} · {fmtDate(record.date)} · Stelle{" "}
              <span className="text-foreground">{record.positionName}</span>
              {record.workplaceName && (
                <>
                  {" "}
                  · Arbeitsplatz <span className="text-foreground">{record.workplaceName}</span>
                </>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Stunden (1–12) *</Label>
            <Input
              type="number"
              min={1}
              max={12}
              step={0.25}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </div>
        </div>
        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={!valid || checkOut.isPending} onClick={() => checkOut.mutate()}>
            Ausbuchen
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
